import { FunctionComponent, useEffect, useState } from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { TableHead, TableRow, TableCell } from '@material-ui/core';
import clsx from 'clsx';
import { SortFields as SortFieldsProducts, SortOrder as SortOrderProducts } from '@spike/product-model';
import { SortFields as SortFieldsVendors, SortOrder as SortOrderVendors } from 'model/ProductVendor';
import { SortFields as SortFieldsCategories, SortOrder as SortOrderCategories } from 'model/ProductCategory';
import TableHeaderProductItem from './TableHeaderProductItem';
import TableHeaderCategoryItem from './TableHeaderCategoryItem';
import TableHeaderVendorItem from './TableHeaderVendorItem';

interface Props {
    selectedView: string;
    onSortProducts: (field: SortFieldsProducts | undefined, order: SortOrderProducts) => void;
    onSortVendors: (field: SortFieldsVendors | undefined, order: SortOrderVendors) => void;
    onSortCategories: (field: SortFieldsCategories | undefined, order: SortOrderCategories) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        cell: {
            paddingLeft: '0px',
            paddingTop: '0px',
            paddingRight: '24px',
            fontWeight: 600,
            borderBottom: '2px solid #000000',
            backgroundColor: '#FAFAFA',
            [theme.breakpoints.down('md')]: {
                paddingBottom: '16px',
                fontSize: '13px'
            },
            [theme.breakpoints.up('lg')]: {
                paddingBottom: '20px',
                fontSize: '16px'
            }
        },
        firstCell: {
            [theme.breakpoints.down(768)]: {
                paddingLeft: 16
            },
            [theme.breakpoints.between(768, 'sm')]: {
                paddingLeft: 27
            },
            [theme.breakpoints.only('md')]: {
                paddingLeft: 43
            },
            [theme.breakpoints.only('lg')]: {
                paddingLeft: 57
            },
            [theme.breakpoints.only('xl')]: {
                paddingLeft: 87
            }
        },
        lastCell: {
            [theme.breakpoints.down(768)]: {
                paddingRight: 16
            },
            [theme.breakpoints.between(768, 'sm')]: {
                paddingRight: 40
            },
            [theme.breakpoints.only('md')]: {
                paddingRight: 65
            },
            [theme.breakpoints.only('lg')]: {
                paddingRight: 87
            },
            [theme.breakpoints.only('xl')]: {
                paddingRight: 132
            }
        },
        productNameCell: {
            width: '34%',
            [theme.breakpoints.down(768)]: {
                paddingLeft: 16
            },
            [theme.breakpoints.between(768, 'sm')]: {
                paddingLeft: 27
            },
            [theme.breakpoints.only('md')]: {
                paddingLeft: 43
            },
            [theme.breakpoints.only('lg')]: {
                paddingLeft: 57
            },
            [theme.breakpoints.only('xl')]: {
                paddingLeft: 87
            }
        },
        productsCells: {
            width: '13%'
        },
        qtyCell: {
            width: '7%',
            textAlign: 'right'
        },
        retailCell: {
            width: '12%'
        },
        actionsCell: {
            width: '8%',
            [theme.breakpoints.down(768)]: {
                paddingRight: 16
            },
            [theme.breakpoints.between(768, 'sm')]: {
                paddingRight: 40
            },
            [theme.breakpoints.only('md')]: {
                paddingRight: 65
            },
            [theme.breakpoints.only('lg')]: {
                paddingRight: 87
            },
            [theme.breakpoints.only('xl')]: {
                paddingRight: 132
            }
        },
        vendorsNameCells: {
            width: '20%'
        },
        vendorsCells: {
            width: '12%'
        },
        vendorsEmailCells: {
            width: '17%'
        },
        vendorsStatusCells: {
            width: '7%'
        },
        vendorsSmallCells: {
            width: '10%'
        },
        categoriesNameCells: {
            width: '35%'
        },
        categoriesCells: {
            width: '25%'
        },
        categoriesSmallCells: {
            width: '15%'
        },
        categoriesActionsCells: {
            width: '10%'
        },
        row: {
            borderBottom: '2px solid #000000'
        }
    })
);

export const TableHeader: FunctionComponent<Props> = props => {
    const classes = useStyles();

    const [sortFieldProducts, setSortFieldProducts] = useState<SortFieldsProducts | undefined>(undefined);
    const [sortOrderProducts, setSortOrderProducts] = useState<SortOrderProducts | undefined>(undefined);

    const [sortFieldVendors, setSortFieldVendors] = useState<SortFieldsVendors | undefined>(undefined);
    const [sortOrderVendors, setSortOrderVendors] = useState<SortOrderVendors | undefined>(undefined);

    const [sortFieldCategories, setSortFieldCategories] = useState<SortFieldsCategories | undefined>(undefined);
    const [sortOrderCategories, setSortOrderCategories] = useState<SortOrderCategories | undefined>(undefined);

    useEffect(() => {
        setSortFieldProducts(undefined);
        setSortOrderProducts(undefined);
        setSortFieldVendors(undefined);
        setSortOrderVendors(undefined);
        setSortFieldCategories(undefined);
        setSortOrderCategories(undefined);
    }, [props.selectedView]);

    const sortHandlerProducts = (field: SortFieldsProducts | undefined, order: SortOrderProducts) => {
        setSortFieldProducts(field);
        setSortOrderProducts(order);
        props.onSortProducts(field, order);
    };

    const sortHandlerVendors = (field: SortFieldsVendors | undefined, order: SortOrderVendors) => {
        setSortFieldVendors(field);
        setSortOrderVendors(order);
        props.onSortVendors(field, order);
    };

    const sortHandlerCategories = (field: SortFieldsCategories | undefined, order: SortOrderCategories) => {
        setSortFieldCategories(field);
        setSortOrderCategories(order);
        props.onSortCategories(field, order);
    };

    const tableProducts = (
        <TableRow className={classes.row}>
            <TableCell className={clsx(classes.cell, classes.productNameCell)}>
                <TableHeaderProductItem
                    label="Product Name"
                    sortField={SortFieldsProducts.PRODUCT_NAME}
                    sortOrder={sortFieldProducts === SortFieldsProducts.PRODUCT_NAME ? sortOrderProducts : undefined}
                    onSort={sortHandlerProducts}
                />
            </TableCell>
            <TableCell className={clsx(classes.cell, classes.productsCells)}>
                <TableHeaderProductItem
                    label="Brand"
                    sortField={SortFieldsProducts.BRAND}
                    sortOrder={sortFieldProducts === SortFieldsProducts.BRAND ? sortOrderProducts : undefined}
                    onSort={sortHandlerProducts}
                />
            </TableCell>
            <TableCell className={clsx(classes.cell, classes.productsCells)}>
                <TableHeaderProductItem
                    label="Category"
                    sortField={SortFieldsProducts.CATEGORY}
                    sortOrder={sortFieldProducts === SortFieldsProducts.CATEGORY ? sortOrderProducts : undefined}
                    onSort={sortHandlerProducts}
                />
            </TableCell>
            <TableCell className={clsx(classes.cell, classes.productsCells)}>
                <TableHeaderProductItem
                    label="Vendor"
                    sortField={SortFieldsProducts.VENDOR}
                    sortOrder={sortFieldProducts === SortFieldsProducts.VENDOR ? sortOrderProducts : undefined}
                    onSort={sortHandlerProducts}
                />
            </TableCell>
            <TableCell className={clsx(classes.cell, classes.qtyCell)}>Qty</TableCell>
            <TableCell className={clsx(classes.cell, classes.retailCell)}>Retail ($)</TableCell>
            <TableCell className={clsx(classes.cell, classes.actionsCell)}></TableCell>
        </TableRow>
    );

    const tableVendors = (
        <TableRow>
            <TableCell className={clsx(classes.cell, classes.firstCell, classes.vendorsNameCells)}>
                <TableHeaderVendorItem
                    label="Vendor Name"
                    sortField={SortFieldsVendors.VENDOR_NAME}
                    sortOrder={sortFieldVendors === SortFieldsVendors.VENDOR_NAME ? sortOrderVendors : undefined}
                    onSort={sortHandlerVendors}
                />
            </TableCell>
            <TableCell className={clsx(classes.cell, classes.vendorsCells)}>
                <TableHeaderVendorItem
                    label="Contact"
                    sortField={SortFieldsVendors.CONTACT}
                    sortOrder={sortFieldVendors === SortFieldsVendors.CONTACT ? sortOrderVendors : undefined}
                    onSort={sortHandlerVendors}
                />
            </TableCell>
            <TableCell className={clsx(classes.cell, classes.vendorsCells)}>
                <TableHeaderVendorItem
                    label="Phone"
                    sortField={SortFieldsVendors.PHONE}
                    sortOrder={sortFieldVendors === SortFieldsVendors.PHONE ? sortOrderVendors : undefined}
                    onSort={sortHandlerVendors}
                />
            </TableCell>
            <TableCell className={clsx(classes.cell, classes.vendorsEmailCells)}>
                <TableHeaderVendorItem
                    label="Email"
                    sortField={SortFieldsVendors.EMAIL}
                    sortOrder={sortFieldVendors === SortFieldsVendors.EMAIL ? sortOrderVendors : undefined}
                    onSort={sortHandlerVendors}
                />
            </TableCell>
            <TableCell className={clsx(classes.cell, classes.vendorsEmailCells)}>
                <TableHeaderVendorItem
                    label="Website"
                    sortField={SortFieldsVendors.WEBSITE}
                    sortOrder={sortFieldVendors === SortFieldsVendors.WEBSITE ? sortOrderVendors : undefined}
                    onSort={sortHandlerVendors}
                />
            </TableCell>
            <TableCell className={clsx(classes.cell, classes.vendorsStatusCells)}>
                <TableHeaderVendorItem
                    label="State"
                    sortField={SortFieldsVendors.STATE}
                    sortOrder={sortFieldVendors === SortFieldsVendors.STATE ? sortOrderVendors : undefined}
                    onSort={sortHandlerVendors}
                />
            </TableCell>
            <TableCell className={clsx(classes.cell, classes.vendorsStatusCells)}>
                <TableHeaderVendorItem
                    label="Products"
                    sortField={SortFieldsVendors.PRODUCTS}
                    sortOrder={sortFieldVendors === SortFieldsVendors.PRODUCTS ? sortOrderVendors : undefined}
                    onSort={sortHandlerVendors}
                />
            </TableCell>
            <TableCell className={clsx(classes.cell, classes.vendorsStatusCells)}>
                <TableHeaderVendorItem
                    label="Status"
                    sortField={SortFieldsVendors.STATUS}
                    sortOrder={sortFieldVendors === SortFieldsVendors.STATUS ? sortOrderVendors : undefined}
                    onSort={sortHandlerVendors}
                />
            </TableCell>
            <TableCell className={clsx(classes.cell, classes.lastCell, classes.vendorsSmallCells)}></TableCell>
        </TableRow>
    );

    const tableCategories = (
        <TableRow>
            <TableCell className={clsx(classes.cell, classes.firstCell, classes.categoriesNameCells)}>
                <TableHeaderCategoryItem
                    label="Category"
                    sortField={SortFieldsCategories.CATEGORY}
                    sortOrder={sortFieldCategories === SortFieldsCategories.CATEGORY ? sortOrderCategories : undefined}
                    onSort={sortHandlerCategories}
                />
            </TableCell>
            <TableCell className={clsx(classes.cell, classes.categoriesSmallCells)}>
                <TableHeaderCategoryItem
                    label="Products"
                    sortField={SortFieldsCategories.PRODUCTS}
                    sortOrder={sortFieldCategories === SortFieldsCategories.PRODUCTS ? sortOrderCategories : undefined}
                    onSort={sortHandlerCategories}
                />
            </TableCell>
            <TableCell className={clsx(classes.cell, classes.categoriesSmallCells)}>Bestsellers</TableCell>
            <TableCell className={clsx(classes.cell, classes.categoriesSmallCells)}>To-do</TableCell>
            <TableCell className={clsx(classes.cell, classes.categoriesSmallCells)}>
                <TableHeaderCategoryItem
                    label="Status"
                    sortField={SortFieldsCategories.STATUS}
                    sortOrder={sortFieldCategories === SortFieldsCategories.STATUS ? sortOrderCategories : undefined}
                    onSort={sortHandlerCategories}
                />
            </TableCell>
            <TableCell className={clsx(classes.cell, classes.lastCell, classes.categoriesSmallCells)}></TableCell>
        </TableRow>
    );

    return (
        <TableHead>
            {props.selectedView === 'products' && tableProducts}
            {props.selectedView === 'vendors' && tableVendors}
            {props.selectedView === 'categories' && tableCategories}
        </TableHead>
    );
};

export default TableHeader;
